import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Play, Tv, Radio, Globe, Package, ArrowRight, Star, Users, Award } from "lucide-react";
import HTMLContent from "@/components/HTMLContent";

const Index = () => {
  const services = [
    {
      icon: <Tv className="h-8 w-8" />,
      title: "Streaming para TV",
      description: "Soluciones completas de streaming para canales de televisión con alta calidad y estabilidad.",
      image: "/images/streaming_equipment_1.jpeg"
    },
    {
      icon: <Radio className="h-8 w-8" />,
      title: "Streaming para Radio",
      description: "Servicios de streaming optimizados para emisoras de radio online con codificación de audio superior.",
      image: "/images/streaming_equipment_2.webp"
    },
    {
      icon: <Play className="h-8 w-8" />,
      title: "Producción de Eventos",
      description: "Producción y streaming profesional para eventos corporativos, sociales y transmisiones en vivo.",
      image: "/images/streaming_equipment_1.jpeg"
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Desarrollo Web",
      description: "Diseño y desarrollo de sitios web profesionales, responsive y optimizados para tu negocio.",
      image: "/images/web_development_1.jpeg"
    }
  ];

  const products = [
    {
      name: "AmelbaBox - Mochila IRL",
      description: "Mochila especializada para streaming móvil",
      price: "$89.99",
      image: "/images/backpack_irl_1.jpeg"
    },
    {
      name: "Kit de Iluminación LED",
      description: "Kit completo para producción audiovisual",
      price: "$199.99",
      image: "/images/streaming_equipment_2.webp"
    }
  ];

  const stats = [
    { icon: <Users className="h-6 w-6" />, value: "500+", label: "Clientes Satisfechos" },
    { icon: <Award className="h-6 w-6" />, value: "1000+", label: "Proyectos Completados" },
    { icon: <Star className="h-6 w-6" />, value: "5★", label: "Calificación Promedio" },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto text-center">
          <Badge className="mb-4 bg-gradient-to-r from-primary to-accent text-white border-0">
            Especialistas en Streaming y Producción Audiovisual
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Amelba Soluciones
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Llevamos tu contenido al siguiente nivel con servicios profesionales de streaming, 
            producción audiovisual y desarrollo web.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/servicios">
              <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity">
                Ver Servicios
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/productos">
              <Button size="lg" variant="outline">
                Explorar Productos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4 text-primary">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nuestros Servicios</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ofrecemos soluciones integrales para todas tus necesidades de streaming y producción audiovisual.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                <CardHeader>
                  <div className="h-48 rounded-lg overflow-hidden mb-4">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="text-primary mb-2">{service.icon}</div>
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription>{service.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/servicios">
              <Button size="lg" variant="outline">
                Ver Todos los Servicios
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Productos Destacados</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Descubre nuestros productos especializados para streaming y producción audiovisual.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {products.map((product, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="h-64 rounded-lg overflow-hidden mb-4">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-bold text-primary">{product.price}</span>
                    <Button size="sm">
                      <Package className="mr-2 h-4 w-4" />
                      Ver Producto
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/productos">
              <Button size="lg" variant="outline">
                Ver Todos los Productos
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-primary to-accent">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Listo para llevar tu proyecto al siguiente nivel?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Contáctanos hoy y descubre cómo podemos ayudarte a crear contenido de calidad profesional.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary">
              Contactar Ahora
            </Button>
            <Link to="/blog">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                Leer Nuestro Blog
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
